export const ERAS = Object.freeze({
  BCE: 'BCE',
  CE: 'CE'
});

const VALID_ERAS = new Set(Object.values(ERAS));

const toPositiveInteger = (value) => {
  if (typeof value === 'number' && Number.isInteger(value)) return value;
  if (typeof value === 'string' && /^\d+$/.test(value)) return Number(value);
  return NaN;
};

const historicalYearToAxisYear = (era, year) => {
  return era === ERAS.BCE ? 1 - year : year;
};

const axisYearToHistoricalYear = (axisYear) => {
  if (!Number.isInteger(axisYear)) {
    throw new Error('Axis year must be an integer.');
  }

  if (axisYear <= 0) {
    return {
      era: ERAS.BCE,
      year: 1 - axisYear
    };
  }

  return {
    era: ERAS.CE,
    year: axisYear
  };
};

const isLeapAxisYear = (axisYear) => {
  if (axisYear % 400 === 0) return true;
  if (axisYear % 100 === 0) return false;
  return axisYear % 4 === 0;
};

const daysInMonth = (axisYear, month) => {
  if (month === 2) return isLeapAxisYear(axisYear) ? 29 : 28;
  if ([4, 6, 9, 11].includes(month)) return 30;
  return 31;
};

export const validateHistoricalDate = (date) => {
  const errors = [];

  if (!date || typeof date !== 'object') {
    return {
      isValid: false,
      errors: ['Date must be an object.']
    };
  }

  const era = date.era;
  const year = toPositiveInteger(date.year);
  const month = date.month === undefined ? 1 : toPositiveInteger(date.month);
  const day = date.day === undefined ? 1 : toPositiveInteger(date.day);

  if (!VALID_ERAS.has(era)) {
    errors.push('Era must be BCE or CE.');
  }

  if (!Number.isInteger(year) || year < 1) {
    errors.push('Year must be a positive integer; historical dates do not have a year zero.');
  }

  if (!Number.isInteger(month) || month < 1 || month > 12) {
    errors.push('Month must be an integer from 1 to 12.');
  }

  if (!Number.isInteger(day) || day < 1 || day > 31) {
    errors.push('Day must be an integer from 1 to 31.');
  }

  if (
    VALID_ERAS.has(era) &&
    Number.isInteger(year) &&
    year >= 1 &&
    Number.isInteger(month) &&
    month >= 1 &&
    month <= 12 &&
    Number.isInteger(day) &&
    day >= 1 &&
    day <= 31
  ) {
    const axisYear = historicalYearToAxisYear(era, year);
    const maxDay = daysInMonth(axisYear, month);
    if (day > maxDay) {
      errors.push(`Day must be between 1 and ${maxDay} for this month.`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export const assertValidHistoricalDate = (date) => {
  const result = validateHistoricalDate(date);
  if (!result.isValid) {
    throw new Error(result.errors.join(' '));
  }
};

export const normalizeHistoricalDate = (date) => {
  assertValidHistoricalDate(date);

  return {
    era: date.era,
    year: toPositiveInteger(date.year),
    month: date.month === undefined ? 1 : toPositiveInteger(date.month),
    day: date.day === undefined ? 1 : toPositiveInteger(date.day)
  };
};

export const toAxisYear = (date) => {
  const normalizedDate = normalizeHistoricalDate(date);
  const axisYear = historicalYearToAxisYear(normalizedDate.era, normalizedDate.year);

  const daysBeforeMonth = Array.from({ length: normalizedDate.month - 1 }).reduce(
    (total, _, monthIndex) => total + daysInMonth(axisYear, monthIndex + 1),
    0
  );
  const dayOfYear = daysBeforeMonth + normalizedDate.day;
  const daysInYear = isLeapAxisYear(axisYear) ? 366 : 365;

  return axisYear + ((dayOfYear - 1) / daysInYear);
};

export const historicalDateToDate = (date) => {
  const normalizedDate = normalizeHistoricalDate(date);
  const axisYear = historicalYearToAxisYear(normalizedDate.era, normalizedDate.year);
  const result = new Date(0);
  result.setFullYear(axisYear, normalizedDate.month - 1, normalizedDate.day);
  result.setHours(0, 0, 0, 0);
  return result;
};

export const dateToHistoricalDate = (dateInput) => {
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(date.getTime())) {
    throw new Error('Date must be a valid Date object, timestamp, or date string.');
  }

  const axisYear = date.getFullYear();
  return {
    ...axisYearToHistoricalYear(axisYear),
    month: date.getMonth() + 1,
    day: date.getDate()
  };
};

export const fromAxisYear = (axisYear) => {
  if (!Number.isFinite(axisYear)) {
    throw new Error('Axis year must be a finite number.');
  }

  const wholeAxisYear = Math.floor(axisYear);
  return axisYearToHistoricalYear(wholeAxisYear);
};

export const formatHistoricalYear = ({ era, year }) => {
  const normalizedYear = toPositiveInteger(year);

  if (!VALID_ERAS.has(era)) {
    throw new Error('Era must be BCE or CE.');
  }

  if (!Number.isInteger(normalizedYear) || normalizedYear < 1) {
    throw new Error('Year must be a positive integer; historical dates do not have a year zero.');
  }

  return `${normalizedYear} ${era}`;
};

export const formatAxisYear = (axisYear) => {
  return formatHistoricalYear(fromAxisYear(axisYear));
};

export const compareHistoricalDates = (left, right) => {
  return toAxisYear(left) - toAxisYear(right);
};
